import { AlertCircle, Clock } from "lucide-react";

import { ColocaStatusPill } from "@/shared/components/data-display/ColocaStatusPill";
import useTranslations from "@/shared/lang/useTranslations";
import { formatDateTime } from "@/shared/utils/dateTimeHelpers";

import { useLinkCountdown } from "../hooks/useLinkCountdown";

export interface KycLinkCountdownPillProps {
  /** ISO 8601 expiry of the link currently on screen. */
  expiresAt: string;
  /** Fired once when the link dies while the page is open, so the host can stop offering it. */
  onExpire?: () => void;
  className?: string;
}

/**
 * "Valid until <date>" for a verification link, as a semantic pill.
 *
 * Deliberately a leaf: it owns the ticking clock, so only this pill re-renders on a tick instead of the whole
 * identity detail page.
 *
 * The absolute date is the headline because it is what an operator acts on — whether to resend now or later.
 * It escalates to a live MM:SS clock only in the final hour, where the exact moment starts to matter; the
 * exact timestamp stays reachable in the tooltip so the two readings never disagree.
 *
 * @summary expiry pill for a KYC verification link, absolute date then countdown
 */
export const KycLinkCountdownPill = ({ expiresAt, onExpire, className }: KycLinkCountdownPillProps) => {
  const { t, activeLanguage } = useTranslations("cards");
  const { isExpired, formattedTime } = useLinkCountdown(expiresAt, onExpire);

  const exactDate = formatDateTime(expiresAt, { locale: activeLanguage, includeTime: true });

  // No readable date leaves nothing honest to assert — `formatDateTime` answers
  // "" for an unparseable value, and the fallback branch below would otherwise
  // render a green "valid until " with a blank date, claiming the opposite of
  // what we actually know.
  if (!exactDate) return null;

  if (isExpired) {
    return (
      <ColocaStatusPill variant="danger" icon={AlertCircle} title={exactDate} className={className}>
        {t("identity_detail_kyc_link_expired")}
      </ColocaStatusPill>
    );
  }

  if (formattedTime) {
    return (
      <ColocaStatusPill variant="warning" icon={Clock} title={exactDate} className={className}>
        {t("identity_detail_kyc_link_expires_in", { time: formattedTime })}
      </ColocaStatusPill>
    );
  }

  return (
    <ColocaStatusPill variant="success" icon={Clock} title={exactDate} className={className}>
      {t("identity_detail_kyc_link_valid_until", { date: exactDate })}
    </ColocaStatusPill>
  );
};
